import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
  CircularProgress,
  useTheme,
  useMediaQuery,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from "@mui/material";
import ecommercePlaceService from "../../../../services/ecommercePlaceService";
import marketplacesService from "../../../../services/marketplacesService";
import axios from "axios";
import PropTypes from "prop-types";
import store from "../../../../redux/store/store";
import { toast } from "react-toastify";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";

const PlacePriceRuleModal = ({
  open,
  onClose,
  priceRuleId,
  onSuccess,
  userId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [ecommercePlaces, setEcommercePlaces] = useState([]);
  const [marketplaces, setMarketplaces] = useState([]);
  const [form, setForm] = useState({
    ecommercePlaceId: "",
    marketPlaceId: "",
    earningsRate: "",
    vatRate: "",
    commission: "",
    cargoFee: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [platformTipi, setPlatformTipi] = useState("");
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);

  useEffect(() => {
    if (open && userId) {
      console.log("userId:", userId);
      setLoading(true);
      Promise.all([
        ecommercePlaceService.getEcommercePlaceAccountsApi(userId),
        marketplacesService.getMarketPlacesAccountsApi(userId),
      ])
        .then(([ecomRes, marketRes]) => {
          console.log("E-Ticaret API response:", ecomRes);
          console.log("Pazaryeri API response:", marketRes);
          setEcommercePlaces(
            Array.isArray(ecomRes?.data?.data?.data)
              ? ecomRes.data.data.data
              : []
          );
          setMarketplaces(
            Array.isArray(marketRes?.data?.data?.data)
              ? marketRes.data.data.data
              : []
          );
        })
        .catch((err) => {
          console.error("API çağrısı hatası:", err);
        })
        .finally(() => setLoading(false));
    }
  }, [open, userId]);

  useEffect(() => {
    if (open && priceRuleId) {
      setAssignmentsLoading(true);
      axios
        .get(`${import.meta.env.VITE_API_URL}/PlacePriceRule`, {
          params: { priceRuleId },
          headers: {
            Authorization: `Bearer ${
              store.getState()?.auth?.user?.accessToken?.token
            }`,
          },
        })
        .then((res) => {
          setAssignments(
            Array.isArray(res?.data?.data?.data) ? res.data.data.data : []
          );
        })
        .catch(() => setAssignments([]))
        .finally(() => setAssignmentsLoading(false));
    }
  }, [open, priceRuleId]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const isFormValid =
    platformTipi &&
    ((platformTipi === "pazaryeri" && form.marketPlaceId) ||
      (platformTipi === "eticaret" && form.ecommercePlaceId)) &&
    form.earningsRate !== "" &&
    form.vatRate !== "" &&
    form.commission !== "" &&
    form.cargoFee !== "";

  const handleSave = async () => {
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const payload = {
        priceRuleId,
        earningsRate: Number(form.earningsRate),
        vatRate: Number(form.vatRate),
        commission: Number(form.commission),
        cargoFee: Number(form.cargoFee),
        createDate: now,
        updateDate: now,
        ia: true,
      };
      if (platformTipi === "pazaryeri") {
        payload.marketPlaceId = Number(form.marketPlaceId);
      } else if (platformTipi === "eticaret") {
        payload.ecommercePlaceId = Number(form.ecommercePlaceId);
      }
      const userToken = store.getState()?.auth?.user?.accessToken?.token;
      await axios.post(
        `${import.meta.env.VITE_API_URL}/PlacePriceRule`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      toast.success("Kayıt başarılı!");
      // Atama sonrası assignments'ı tekrar çek
      const res = await axios.get(
        `${import.meta.env.VITE_API_URL}/PlacePriceRule`,
        {
          params: { priceRuleId },
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        }
      );
      setAssignments(
        Array.isArray(res?.data?.data?.data) ? res.data.data.data : []
      );
      if (onSuccess) onSuccess();
      // Formu sıfırla
      setForm({
        ecommercePlaceId: "",
        marketPlaceId: "",
        earningsRate: "",
        vatRate: "",
        commission: "",
        cargoFee: "",
      });
      setPlatformTipi("");
    } catch (err) {
      toast.error("Kayıt sırasında bir hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  // E-Ticaret platformları için benzersiz liste oluştur
  const uniqueEcommercePlaces = [];
  const seenEcommerceIds = new Set();
  for (const item of ecommercePlaces) {
    if (!seenEcommerceIds.has(item.ecommercePlaceId)) {
      uniqueEcommercePlaces.push({
        id: item.ecommercePlaceId,
        name: item.ecommercePlaceName,
      });
      seenEcommerceIds.add(item.ecommercePlaceId);
    }
  }

  // Pazaryeri platformları için benzersiz liste oluştur
  const uniqueMarketplaces = [];
  const seenMarketplaceIds = new Set();
  for (const item of marketplaces) {
    if (!seenMarketplaceIds.has(item.marketPlaceId)) {
      uniqueMarketplaces.push({
        id: item.marketPlaceId,
        name: item.marketPlaceName,
      });
      seenMarketplaceIds.add(item.marketPlaceId);
    }
  }

  // Atanmış platformu silme fonksiyonu
  const handleDeleteAssignment = async (assignmentId) => {
    try {
      const userToken = store.getState()?.auth?.user?.accessToken?.token;
      await axios.delete(`${import.meta.env.VITE_API_URL}/PlacePriceRule`, {
        params: { id: assignmentId },
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      });
      toast.success("Platform ataması silindi");
      // Silme sonrası tekrar assignments'ı güncelle
      setAssignments(assignments.filter((a) => a.id !== assignmentId));
    } catch (err) {
      toast.error("Silme işlemi sırasında bir hata oluştu.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
              wordBreak: "break-word",
            }}
          >
            Fiyat Kuralı - Pazar Yeri ve E-Ticaret Atama
          </Typography>
          {isMobile && (
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={120}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel id="platformTipi-label">Platform Tipi</InputLabel>
                <Select
                  labelId="platformTipi-label"
                  name="platformTipi"
                  value={platformTipi}
                  label="Platform Tipi"
                  onChange={(e) => {
                    setPlatformTipi(e.target.value);
                    setForm({
                      ...form,
                      ecommercePlaceId: "",
                      marketPlaceId: "",
                    });
                  }}
                >
                  <MenuItem value="pazaryeri">Pazaryeri</MenuItem>
                  <MenuItem value="eticaret">E-Ticaret</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {platformTipi === "pazaryeri" && (
              <Grid item xs={12}>
                <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                  <InputLabel id="marketPlaceId-label">Pazar Yeri</InputLabel>
                  <Select
                    labelId="marketPlaceId-label"
                    name="marketPlaceId"
                    value={form.marketPlaceId}
                    label="Pazar Yeri"
                    onChange={handleChange}
                  >
                    {uniqueMarketplaces.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {platformTipi === "eticaret" && (
              <Grid item xs={12}>
                <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                  <InputLabel id="ecommercePlaceId-label">
                    E-Ticaret Platformu
                  </InputLabel>
                  <Select
                    labelId="ecommercePlaceId-label"
                    name="ecommercePlaceId"
                    value={form.ecommercePlaceId}
                    label="E-Ticaret Platformu"
                    onChange={handleChange}
                  >
                    {uniqueEcommercePlaces.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {(platformTipi === "pazaryeri" || platformTipi === "eticaret") && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="earningsRate"
                    label="Kazanç Oranı (%)"
                    type="number"
                    value={form.earningsRate}
                    onChange={handleChange}
                    InputProps={{
                      inputProps: { min: 0 },
                      size: isMobile ? "small" : "medium",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 0.5,
                      color: "text.secondary",
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    İlgili kategorilerdeki ürünlerin satış fiyatı üzerinden
                    yüzde kaç kar edilmek istendiği
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="vatRate"
                    label="KDV Oranı (%)"
                    type="number"
                    value={form.vatRate}
                    onChange={handleChange}
                    InputProps={{
                      inputProps: { min: 0 },
                      size: isMobile ? "small" : "medium",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 0.5,
                      color: "text.secondary",
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    Atanacak kategorilerin KDV oranı
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="commission"
                    label="Komisyon (%)"
                    type="number"
                    value={form.commission}
                    onChange={handleChange}
                    InputProps={{
                      inputProps: { min: 0 },
                      size: isMobile ? "small" : "medium",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 0.5,
                      color: "text.secondary",
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    Atanacak kategorilerin pazaryeri komisyon oranı
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="cargoFee"
                    label="Kargo Ücreti"
                    type="number"
                    value={form.cargoFee}
                    onChange={handleChange}
                    InputProps={{
                      inputProps: { min: 0 },
                      size: isMobile ? "small" : "medium",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 0.5,
                      color: "text.secondary",
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    Atanacak kategorilerin kargo ücreti (barem kuralları
                    tanımlıdır, eğer barem içerisinde ise ürün fiyatı tanımlı
                    olan barem kargo ücreti yansır)
                  </Typography>
                </Grid>
              </>
            )}
            {/* Atama Listesi */}
            {assignmentsLoading ? (
              <Box sx={{ my: 2, textAlign: "center" }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              assignments.length > 0 && (
                <Box sx={{ my: 2, width: "100%", overflowX: "auto" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1,
                      fontWeight: "bold",
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                  >
                    Bu kurala atanmış platformlar:
                  </Typography>
                  <TableContainer
                    component={Paper}
                    sx={{ maxHeight: { xs: 200, sm: 300 } }}
                  >
                    <Table size={isMobile ? "small" : "medium"} stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Platform Adı</TableCell>
                          <TableCell>Tip</TableCell>
                          <TableCell>Kazanç Oranı (%)</TableCell>
                          <TableCell>KDV (%)</TableCell>
                          <TableCell>Komisyon (%)</TableCell>
                          <TableCell>Kargo Ücreti</TableCell>
                          <TableCell>Durum</TableCell>
                          <TableCell align="center">İşlem</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {assignments.map((a) => (
                          <TableRow key={a.id}>
                            <TableCell>
                              {a.marketPlaceName || a.ecommercePlaceName}
                            </TableCell>
                            <TableCell>
                              {a.marketPlaceId
                                ? "Pazaryeri"
                                : a.ecommercePlaceId
                                ? "E-Ticaret"
                                : "-"}
                            </TableCell>
                            <TableCell>{a.earningsRate}</TableCell>
                            <TableCell>{a.vatRate}</TableCell>
                            <TableCell>{a.commission}</TableCell>
                            <TableCell>{a.cargoFee}</TableCell>
                            <TableCell>
                              <Typography
                                sx={{
                                  color: a.ia ? "success.main" : "error.main",
                                  fontWeight: "medium",
                                }}
                              >
                                {a.ia ? "Aktif" : "Pasif"}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Sil">
                                <IconButton
                                  size={isMobile ? "small" : "medium"}
                                  color="error"
                                  onClick={() => handleDeleteAssignment(a.id)}
                                >
                                  <DeleteIcon
                                    fontSize={isMobile ? "small" : "medium"}
                                  />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
        <Button
          onClick={onClose}
          disabled={saving}
          fullWidth={isMobile}
          sx={{ mb: { xs: 1, sm: 0 } }}
        >
          İptal
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || loading || !isFormValid}
          fullWidth={isMobile}
        >
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PlacePriceRuleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  priceRuleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onSuccess: PropTypes.func,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default PlacePriceRuleModal;
